import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import apiClient from '../../api/api';
import './RechargeModal.css';

// Initialize Stripe
// Replace with your actual publishable key or use env var
// Initialize Stripe
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!STRIPE_KEY) {
  console.error('Stripe Publishable Key is missing! Check your .env file.');
}

const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null;

// Internal component for the actual payment form (Stripe Elements)
const PaymentForm = ({ amount, onSuccess, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin + '/wallet', // Not really used if we handle redirect: if_required
      },
      redirect: 'if_required' 
    });

    if (result.error) {
      setError(result.error.message);
      setProcessing(false);
    } else {
      // Payment succeeded
      if (result.paymentIntent.status === 'succeeded') {
        try {
          await apiClient.post('/wallet/verify-recharge', {
            paymentIntentId: result.paymentIntent.id
          });
          setProcessing(false);
          onSuccess();
        } catch (err) {
          setError('Payment succeeded but verification failed. Please contact support.');
          setProcessing(false);
        }
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-4">
        <p className="mb-2 font-semibold">Amount to Pay: ${amount}</p>
        <PaymentElement />
      </div>
      {error && <div className="text-red-500 mb-2 text-sm">{error}</div>}
      <button 
        type="submit" 
        disabled={!stripe || processing}
        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 w-full mb-2"
      >
        {processing ? 'Processing...' : `Pay $${amount}`}
      </button>
      <button 
        type="button"
        onClick={onBack}
        className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400 w-full"
      >
        Back
      </button>
    </form>
  );
};

const RechargeModal = ({ isOpen, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  if (!stripePromise) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
           <h3 className="text-xl font-bold mb-4 text-red-600">Configuration Error</h3>
           <p>Stripe Publishable Key is missing.</p>
           <p className="text-sm text-gray-600 mt-2">Please check your frontend <code>.env</code> file and ensure <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> is set.</p>
           <button onClick={onClose} className="mt-4 bg-gray-300 px-4 py-2 rounded">Close</button>
        </div>
      </div>
    );
  }

  const handleAmountChange = (e) => {
    setAmount(e.target.value);
    setError(null);
  };

  const initPayment = async () => {
    if (!amount || isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post('/wallet/create-payment-intent', {
        amount: parseFloat(amount),
        currency: 'usd'
      });
      setClientSecret(response.data.clientSecret);
      setLoading(false);
    } catch (err) {
      console.error('Error creating payment intent:', err);
      const serverMsg = err.response?.data?.message;
      const serverError = err.response?.data?.error;
      const debugMsg = serverError ? ` (${serverError})` : '';
      setError(serverMsg ? `${serverMsg}${debugMsg}` : 'Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setAmount('');
    setClientSecret('');
    setError(null);
    onClose();
  };

  const handleSuccess = () => {
    setAmount('');
    setClientSecret('');
    setError(null);
    onSuccess();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4 border-b pb-2">Recharge Wallet</h3>

        {!clientSecret ? (
          <div className="amount-input-section">
            <label className="block mb-2 font-medium">Enter Amount (USD):</label>
            <input 
              type="number" 
              value={amount} 
              onChange={handleAmountChange} 
              placeholder="Ex: 50.00"
              className="p-2 border rounded mb-4 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {error && <div className="text-red-500 mb-4 text-sm">{error}</div>}
            
            <button 
              onClick={initPayment} 
              disabled={loading || !amount}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 w-full transition"
            >
              {loading ? 'Initializing...' : 'Proceed to Pay'}
            </button>
            <button onClick={handleClose} className="mt-4 text-gray-500 underline text-sm w-full text-center">
              Cancel
            </button>
          </div>
        ) : (
          <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
            <PaymentForm 
              amount={amount} 
              onSuccess={handleSuccess} 
              onBack={() => setClientSecret('')} 
            />
          </Elements>
        )}
      </div>
    </div>
  );
};

export default RechargeModal;
